import "./SwipeableModals.scss";
import React, { useState, useEffect } from "react";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import {
  FormControl,
  InputLabel,
  OutlinedInput,
  IconButton,
} from "@mui/material";
import AddPhoto from "./../../../assets/add-photo.png";
import BlueButton from "../../../components/BlueButton/BlueButton";
import api from "../../../utils/api";
import CloseIcon from "@mui/icons-material/Close"; // Import CloseIcon for remove functionality
import { toast } from "react-toastify";
import { photosApi } from "../../../SourceData";

const SwipeableModals = ({
  open,
  onClose,
  onOpen,
  currentView,
  categoryData,
  onDataChange,
}) => {
  // Form state
  const [image, setImage] = useState(null);
  const [nameAr, setNameAr] = useState("");
  const [nameEn, setNameEn] = useState("");
  const [descriptionAr, setDescriptionAr] = useState(""); // New state for Arabic description
  const [descriptionEn, setDescriptionEn] = useState(""); // New state for English description
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  // Reset form when modal opens or category changes
  useEffect(() => {
    if (categoryData && currentView === "editCategory") {
      setNameAr(categoryData.name.ar || "");
      setNameEn(categoryData.name.en || "");
      // Set descriptions if available
      setDescriptionAr(categoryData.description?.ar || "");
      setDescriptionEn(categoryData.description?.en || "");

      // Set image preview if available
      if (categoryData.photo) {
        setImagePreview(categoryData.photo);
      } else {
        setImagePreview(null);
      }
    } else if (currentView === "createCategory") {
      // Reset form for create mode
      setNameAr("");
      setNameEn("");
      setDescriptionAr("");
      setDescriptionEn("");
      setImage(null);
      setImagePreview(null);
    }
  }, [categoryData, currentView, open]);

  // Handle image change
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  // Handle remove image
  const handleRemoveImage = () => {
    setImage(null);
    setImagePreview(null);

    // Reset the file input
    const fileInput = document.getElementById("image");
    if (fileInput) {
      fileInput.value = "";
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("name[ar]", nameAr);
      formData.append("name[en]", nameEn);
      formData.append("description[ar]", descriptionAr);
      formData.append("description[en]", descriptionEn);

      if (image) {
        formData.append("photo", image);
      }

      let response;

      if (currentView === "editCategory" && categoryData?._id) {
        // Update existing category
        response = await api.patch(
          `/categories/${categoryData._id}`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );
      } else {
        // Create new category
        response = await api.post("/categories", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      if (response.data.status === "success") {
        onDataChange();
        onClose();
        toast.success(
          currentView === "editCategory"
            ? "تم تعديل القسم بنجاح"
            : "تم انشاء القسم بنجاح"
        );
      }
    } catch (error) {
      console.error("Error saving category:", error);
    } finally {
      setLoading(false);
    }
  };

  // Render Create/Edit Category View
  const renderCategoryForm = () => {
    const isEdit = currentView === "editCategory";
    const title = isEdit ? "تعديل الفئة" : "اضافة فئة جديدة";
    const buttonText = isEdit ? "تعديل" : "اضافة";

    return (
      <div className="user-modal">
        <p className="user-modal-p">{title}</p>
        <form onSubmit={handleSubmit}>
          <div className="form-img" style={{ position: "relative" }}>
            <label htmlFor="image" className="img-control">
              <img
                src={
                  imagePreview
                    ? photosApi + imagePreview
                    : image instanceof File
                    ? URL.createObjectURL(image)
                    : AddPhoto
                }
                alt=""
              />
              <span>اضافة صورة الشعار</span>
            </label>
            <input type="file" id="image" onChange={handleImageChange} />

            {/* Remove photo icon - only show when there is an image */}
            {(imagePreview || image) && (
              <IconButton
                onClick={handleRemoveImage}
                className="remove-photo-btn"
                size="small"
                sx={{
                  position: "absolute",
                  top: "5px",
                  right: "5px",
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                  },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </div>

          {/* اسم الفئة بالعربية */}
          <FormControl sx={{ width: "100%" }} variant="outlined">
            <InputLabel
              htmlFor="name-ar"
              shrink
              sx={{
                background: "white",
                transformOrigin: "right top",
                transform: "translate(14px, -9px) scale(0.75)",
                right: 24,
                left: "auto",
                paddingLeft: "4px",
                paddingRight: "4px",
              }}
            >
              اسم القسم بالعربية
            </InputLabel>
            <OutlinedInput
              sx={{ borderRadius: "8px" }}
              required
              id="name-ar"
              type="text"
              notched
              value={nameAr}
              onChange={(e) => setNameAr(e.target.value)}
            />
          </FormControl>

          {/* اسم الفئة بالانجليزية */}
          <FormControl sx={{ width: "100%" }} variant="outlined">
            <InputLabel
              htmlFor="name-en"
              shrink
              sx={{
                background: "white",
                transformOrigin: "right top",
                transform: "translate(14px, -9px) scale(0.75)",
                right: 24,
                left: "auto",
                paddingLeft: "4px",
                paddingRight: "4px",
              }}
            >
              اسم القسم بالانجليزية
            </InputLabel>
            <OutlinedInput
              sx={{ borderRadius: "8px" }}
              required
              id="name-en"
              type="text"
              notched
              value={nameEn}
              onChange={(e) => setNameEn(e.target.value)}
            />
          </FormControl>

          {/* وصف الفئة بالعربية */}
          <FormControl sx={{ width: "100%" }} variant="outlined">
            <InputLabel
              htmlFor="description-ar"
              shrink
              sx={{
                background: "white",
                transformOrigin: "right top",
                transform: "translate(14px, -9px) scale(0.75)",
                right: 24,
                left: "auto",
                paddingLeft: "4px",
                paddingRight: "4px",
              }}
            >
              وصف القسم بالعربية
            </InputLabel>
            <OutlinedInput
              sx={{ borderRadius: "8px" }}
              id="description-ar"
              type="text"
              multiline
              rows={3}
              notched
              value={descriptionAr}
              onChange={(e) => setDescriptionAr(e.target.value)}
            />
          </FormControl>

          {/* وصف الفئة بالانجليزية */}
          <FormControl sx={{ width: "100%" }} variant="outlined">
            <InputLabel
              htmlFor="description-en"
              shrink
              sx={{
                background: "white",
                transformOrigin: "right top",
                transform: "translate(14px, -9px) scale(0.75)",
                right: 24,
                left: "auto",
                paddingLeft: "4px",
                paddingRight: "4px",
              }}
            >
              وصف القسم بالانجليزية
            </InputLabel>
            <OutlinedInput
              sx={{ borderRadius: "8px" }}
              id="description-en"
              type="text"
              multiline
              rows={3}
              notched
              value={descriptionEn}
              onChange={(e) => setDescriptionEn(e.target.value)}
            />
          </FormControl>

          <BlueButton
            text={buttonText}
            size="medium"
            variant="fill"
            type="submit"
            loading={loading}
          />
        </form>
      </div>
    );
  };

  const renderDefaultView = () => <div></div>;

  const views = {
    createCategory: renderCategoryForm,
    editCategory: renderCategoryForm,
  };

  return (
    <SwipeableDrawer
      anchor="left"
      open={open}
      onClose={onClose}
      onOpen={onOpen}
      sx={{ zIndex: "1202" }}
      disableScrollLock
    >
      {(views[currentView] || renderDefaultView)()}
    </SwipeableDrawer>
  );
};

export default SwipeableModals;
